import { Skeleton, Stack, Typography } from "@mui/material";
import { useMemo, type FC } from "react";
import type { UseSupabaseLoaderState } from "../../../server/supabase/hooks/types";
import type { ErrorProps } from "../../../utils/errors/extract-zod-tree-error-props";
import { useFlags } from "../../../server/optimizely";
import { FullScreenSidebar } from "../../generic/components/full-screen-sidebar";
import { classifyMomentWhen } from "../../moment-card/components/moment-card-when";
import type { FollowUpMomentDetailsLoaderData } from "../hooks/use-follow-up-moment-details";
import type { FollowUp } from "../server/follow-up-service";
import {
  extractDynamicOnlyLinks,
  extractDynamicOnlySurveyIds,
  type DynamicLinkSource,
} from "../utils/dynamic-link-utils";
import { FollowUpSidebarMomentSummary } from "./follow-up-sidebar-moment-summary";
import { FollowUpSidebarReason } from "./follow-up-sidebar-reason";

export const FollowUpSidebar: FC<{
  followUpMomentDetailsState: UseSupabaseLoaderState<FollowUpMomentDetailsLoaderData>;
  reason: FollowUp["config"];
  setReason: (reason: FollowUp["config"]) => void;
  error: ErrorProps;
  disabled: boolean;
  showLinkClick: boolean;
}> = ({ followUpMomentDetailsState, reason, setReason, error, disabled, showLinkClick }) => {
  const classified = classifyMomentWhen(followUpMomentDetailsState.data?.momentFollowUpDetails);

  const {
    flags: { cha_4255_dynamic_link_warning },
  } = useFlags("cha_4255_dynamic_link_warning");

  const source = useMemo((): DynamicLinkSource | null => {
    if (!cha_4255_dynamic_link_warning) return null;
    const data = followUpMomentDetailsState.data;
    if (!data) return null;
    if (data.useLexicalEditor && data.momentFollowUpDetails.design)
      return { type: "lexical", design: data.momentFollowUpDetails.design };
    const sourceEmail = data.followUp?.sourceEmailMoment ?? data.sourceEmailMoment;
    if (sourceEmail?.body) return { type: "email_html", html: sourceEmail.body };
    if (sourceEmail?.design) return { type: "unlayer", design: sourceEmail.design };
    return null;
  }, [cha_4255_dynamic_link_warning, followUpMomentDetailsState.data]);

  const dynamicOnlyLinks = useMemo(() => extractDynamicOnlyLinks(source), [source]);
  const dynamicOnlySurveyIds = useMemo(() => extractDynamicOnlySurveyIds(source), [source]);

  return (
    <FullScreenSidebar open={true}>
      <Stack spacing={2} direction="column">
        <Typography variant="subtitle2">Original Communication</Typography>
        {followUpMomentDetailsState.loading && (
          <Skeleton variant="rectangular" width="100%" height={200} />
        )}
        {!followUpMomentDetailsState.loading && followUpMomentDetailsState.data && (
          <FollowUpSidebarMomentSummary
            followUpMomentDetails={followUpMomentDetailsState.data.momentFollowUpDetails}
            whenClassification={classified}
          />
        )}
        {followUpMomentDetailsState.data && (
          <FollowUpSidebarReason
            whenClassification={classified}
            value={reason}
            onChange={setReason}
            source_id={followUpMomentDetailsState.data.momentFollowUpDetails.id}
            surveys={followUpMomentDetailsState.data.momentFollowUpDetails.surveys}
            error={error}
            disabled={disabled}
            showLinkClick={showLinkClick}
            hyperlinks={followUpMomentDetailsState.data.momentFollowUpDetails.hyperlinks ?? []}
            dynamicOnlyLinks={dynamicOnlyLinks}
            dynamicOnlySurveyIds={dynamicOnlySurveyIds}
          />
        )}
      </Stack>
    </FullScreenSidebar>
  );
};
